import React from "react";
import { getTickerDuration } from "../../utils/tickerMotion";
import "./LoadingTicker.css";

const TICKER_CONTENT = {
  loading: {
    ariaLabel: "در حال بارگذاری",
    text: "در حال بارگذاری",
  },
  noSources: {
    ariaLabel: "هیچ منبعی انتخاب نشده",
    text: "حداقل یک منبع انتخاب کنید",
  },
  error: {
    ariaLabel: "اتصال اینترنت خود را بررسی کنید",
    text: "اتصال اینترنت خود را بررسی کنید",
  },
};

const LoadingTicker = ({ variant = "loading", ariaLabel, text }) => {
  const content = TICKER_CONTENT[variant] || TICKER_CONTENT.loading;
  const tickerAriaLabel = ariaLabel ?? content.ariaLabel;
  const tickerText = text ?? content.text;

  const containerRef = React.useRef(null);
  const groupRef = React.useRef(null);

  const [tickerLayout, setTickerLayout] = React.useState({
    copies: 2,
    distance: 0,
  });

  const loadingItems = React.useMemo(() => Array.from({ length: 16 }), []);

  React.useLayoutEffect(() => {
    let isActive = true;

    const updateLayout = () => {
      if (!isActive) return;

      const containerWidth = containerRef.current?.offsetWidth || 0;
      const groupWidth = groupRef.current?.scrollWidth || 0;

      if (!containerWidth || !groupWidth) return;

      const measuredDistance = Math.ceil(groupWidth);
      const neededCopies = Math.min(
        8,
        Math.max(2, Math.ceil(containerWidth / measuredDistance) + 2)
      );

      setTickerLayout((current) =>
        current.copies === neededCopies &&
        current.distance === measuredDistance
          ? current
          : { copies: neededCopies, distance: measuredDistance }
      );
    };

    updateLayout();

    const rafId =
      typeof requestAnimationFrame === "function"
        ? requestAnimationFrame(updateLayout)
        : null;

    if (typeof document !== "undefined" && document.fonts?.ready) {
      document.fonts.ready.then(updateLayout).catch(() => {});
    }

    if (typeof ResizeObserver === "undefined") {
      if (typeof window !== "undefined") {
        window.addEventListener("resize", updateLayout);
      }

      return () => {
        isActive = false;

        if (rafId !== null && typeof cancelAnimationFrame === "function") {
          cancelAnimationFrame(rafId);
        }

        if (typeof window !== "undefined") {
          window.removeEventListener("resize", updateLayout);
        }
      };
    }

    const observer = new ResizeObserver(updateLayout);

    if (containerRef.current) observer.observe(containerRef.current);
    if (groupRef.current) observer.observe(groupRef.current);

    return () => {
      isActive = false;

      if (rafId !== null && typeof cancelAnimationFrame === "function") {
        cancelAnimationFrame(rafId);
      }

      observer.disconnect();
    };
  }, [tickerText]);

  const tickerGroups = Array.from(
    { length: tickerLayout.copies },
    (_, groupIndex) => groupIndex
  );

  return (
    <div
      ref={containerRef}
      className={`loading-ticker loading-ticker--${variant}`}
      aria-label={tickerAriaLabel}
    >
      <div
        className="loading-ticker__track"
        style={{
          "--loading-ticker-distance": `${tickerLayout.distance}px`,
          "--loading-ticker-duration": getTickerDuration(
            tickerLayout.distance
          ),
        }}
      >
        {tickerGroups.map((groupIndex) => (
          <div
            key={groupIndex}
            ref={groupIndex === 0 ? groupRef : null}
            className="loading-ticker__group"
            aria-hidden={groupIndex > 0}
          >
            {loadingItems.map((_, index) => (
              <span key={index} className="loading-ticker__item">
                {tickerText}
              </span>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

export default LoadingTicker;
